'use client'

import { Toolbar as ToolbarPrimitive } from '@base-ui/react/toolbar'
import * as React from 'react'
import { cn } from '../lib/utils'
import {
  Tooltip as TooltipPrimitive,
  TooltipTrigger,
  TooltipPopup,
  TooltipCreateHandle as createTooltipHandle,
} from './tooltip'

const ToolbarContext = React.createContext<{
  tooltipHandle: ReturnType<typeof createTooltipHandle<React.ReactNode>>
} | null>(null)

function Toolbar({
  className,
  variant = 'default',
  size = 'default',
  tooltipSide = 'top',
  ...props
}: ToolbarPrimitive.Root.Props & {
  variant?: 'default' | 'docked' | 'floating'
  size?: 'default' | 'sm'
  tooltipSide?: React.ComponentProps<typeof TooltipPopup>['side']
}) {
  const tooltipHandle = createTooltipHandle<React.ReactNode>()
  const toolbarCtxValue = React.useMemo(
    () => ({ tooltipHandle }),
    [tooltipHandle],
  )

  return (
    <ToolbarContext.Provider value={toolbarCtxValue}>
      <ToolbarPrimitive.Root
        className={cn(
          'text-on-surface bg-surface-2 relative flex gap-2 p-2 not-dark:bg-clip-padding data-[orientation=vertical]:flex-col',
          variant === 'default' && 'rounded-xl border',
          variant === 'docked' && 'min-h-16 p-4',
          variant === 'floating' &&
            'rounded-full border not-dark:shadow-sm [&_[data-slot=toolbar-button]]:rounded-full!',
          size === 'sm' && 'gap-1 p-1 [&_[data-slot=toolbar-button]]:size-8 [&_[data-slot=toolbar-button]_svg]:size-4',
          className,
        )}
        data-slot="toolbar"
        data-variant={variant}
        data-size={size}
        {...props}
      />
      <TooltipPrimitive handle={tooltipHandle}>
        {({ payload }) => (
          <TooltipPopup side={tooltipSide}>{payload}</TooltipPopup>
        )}
      </TooltipPrimitive>
    </ToolbarContext.Provider>
  )
}

function ToolbarButton({
  className,
  tooltip,
  ...props
}: ToolbarPrimitive.Button.Props & { tooltip?: React.ReactNode }) {
  const ctx = React.useContext(ToolbarContext)

  if (tooltip && ctx) {
    return (
      <TooltipTrigger
        handle={ctx.tooltipHandle}
        payload={tooltip}
        render={(triggerProps) => (
          <ToolbarPrimitive.Button
            {...triggerProps}
            className={cn(
              'inline-flex size-12 cursor-pointer items-center justify-center disabled:cursor-not-allowed',
              className,
            )}
            data-slot="toolbar-button"
            {...props}
          />
        )}
      />
    )
  }

  return (
    <ToolbarPrimitive.Button
      className={cn(
        'inline-flex size-12 cursor-pointer items-center justify-center disabled:cursor-not-allowed',
        className,
      )}
      data-slot="toolbar-button"
      {...props}
    />
  )
}

function ToolbarLink({ className, ...props }: ToolbarPrimitive.Link.Props) {
  return (
    <ToolbarPrimitive.Link
      className={cn(className)}
      data-slot="toolbar-link"
      {...props}
    />
  )
}

function ToolbarInput({ className, ...props }: ToolbarPrimitive.Input.Props) {
  return (
    <ToolbarPrimitive.Input
      className={cn(className)}
      data-slot="toolbar-input"
      {...props}
    />
  )
}

function ToolbarGroup({ className, ...props }: ToolbarPrimitive.Group.Props) {
  return (
    <ToolbarPrimitive.Group
      className={cn(
        'flex items-center gap-1 data-[orientation=vertical]:flex-col',
        className,
      )}
      data-slot="toolbar-group"
      {...props}
    />
  )
}

function ToolbarSeparator({
  className,
  ...props
}: ToolbarPrimitive.Separator.Props) {
  return (
    <ToolbarPrimitive.Separator
      className={cn(
        "bg-separator shrink-0 rounded-full data-[orientation=horizontal]:my-0.5 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:my-1.5 data-[orientation=vertical]:w-px data-[orientation=vertical]:not-[[class^='h-']]:not-[[class*='_h-']]:self-stretch",
        className,
      )}
      data-slot="toolbar-separator"
      {...props}
    />
  )
}

function useToolbarContext() {
  return React.useContext(ToolbarContext)
}

export {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
  ToolbarButton,
  ToolbarLink,
  ToolbarInput,
  useToolbarContext,
}
